import { alpha, decomposeColor, hslToRgb } from "@mui/material/styles";
import { isDarkTheme } from "./themeTraits";

/**
 * A tenant colour at an opacity. Every colour here comes straight from the tenant row, so a
 * malformed one would otherwise throw out of alpha() and take the whole page down with it;
 * a tint that fails is simply not painted.
 */
export const tint = (color, opacity) => {
  try {
    return alpha(color, opacity);
  } catch {
    return "transparent";
  }
};

// The logo fills no letter flat: its "VR" runs blue into cyan and its light arc runs orange
// into amber. Neither pair is a plain lightening — both walk the hue on towards the bright
// end of their own half of the wheel — so that is how the lit end of a text gradient is
// derived here, which lands within a shade of the logo's own two pairs.
const HUE_SHIFT_DEG = 12;
const SATURATION_LIFT = 0.1;
const LIGHTNESS_LIFT = 0.06;

// Below this a colour has no hue to walk: white, black and grey keep a flat fill, which is
// what confines the gradient to the accent-coloured text.
const FLAT_BELOW_SATURATION = 0.15;

const clamp = (value) => Math.min(1, Math.max(0, value));

const toHsl = (color) => {
  const [red, green, blue] = decomposeColor(color).values.map((value) => value / 255);
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const lightness = (max + min) / 2;
  const chroma = max - min;

  if (chroma === 0) return { hue: 0, saturation: 0, lightness };

  const hue = max === red
    ? ((green - blue) / chroma) % 6
    : max === green
      ? (blue - red) / chroma + 2
      : (red - green) / chroma + 4;

  return {
    hue: (hue * 60 + 360) % 360,
    saturation: chroma / (1 - Math.abs(2 * lightness - 1)),
    lightness,
  };
};

const litEnd = ({ hue, saturation, lightness }) => {
  // Cool colours brighten towards cyan, warm ones towards yellow — never past it, so a
  // colour already sitting on the bright hue only gains light.
  const brightHue = hue > 120 && hue < 300 ? 180 : 60;
  const shift = Math.sign(brightHue - hue) * Math.min(HUE_SHIFT_DEG, Math.abs(brightHue - hue));

  return hslToRgb(`hsl(${hue + shift}, ` +
    `${clamp(saturation + SATURATION_LIFT) * 100}%, ` +
    `${clamp(lightness + LIGHTNESS_LIFT) * 100}%)`);
};

/**
 * The two ends of the fill a colour is painted with, or `null` when it has to stay flat.
 *
 * Flat whenever a gradient would be wrong or unreadable: on paper, on white/black/grey, and on a
 * colour the tenant row got wrong. Both dark themes take it: on herevr it is the logo's light, on
 * 2fadedbarbershop the sheen of its gold lettering.
 */
const gradientEnds = (displayConfig, color) => {
  if (!color) return null;
  if (!isDarkTheme(displayConfig)) return null;

  try {
    const hsl = toHsl(color);

    if (hsl.saturation < FLAT_BELOW_SATURATION) return null;

    return { base: color, lit: litEnd(hsl) };
  } catch {
    return null;
  }
};

const darkened = (color, percent) => `color-mix(in srgb, ${color} ${100 - percent}%, black)`;

/**
 * Fills text with a gradient of its own colour instead of a flat one. `background-clip:
 * text` paints the letters themselves, not a box behind them, so this belongs in `sx` and
 * replaces the `color` prop of whatever it is spread onto.
 *
 * Falls back to the flat colour it was given whenever `gradientEnds` says the colour has
 * no gradient in it, and under forced colours.
 *
 * An icon inside the styled element is **not** text: it inherits the transparent fill and
 * disappears. Give it a colour of its own -- see `navButtonSx` in the header.
 */
export const gradientTextSx = (displayConfig, color) => {
  if (!color) return {};

  const ends = gradientEnds(displayConfig, color);

  if (!ends) return { color };

  return {
    color: "transparent",
    backgroundImage: `linear-gradient(160deg, ${ends.base} 0%, ${ends.lit} 100%)`,
    WebkitBackgroundClip: "text",
    backgroundClip: "text",
    WebkitTextFillColor: "transparent",
    // Forced colours drop the background image but leave the transparent fill in place,
    // which would erase the text; hand those users the flat colour back.
    "@media (forced-colors: active)": {
      backgroundImage: "none",
      WebkitTextFillColor: "currentcolor",
      color,
    },
  };
};

/**
 * The same two ends as a surface fill rather than a letter fill: what a filled button is
 * painted with, so an action carries the gradient its headings do. `hover` is what a
 * button wants and a disc does not: it darkens both ends on pointer-over. The flat colour stays
 * underneath the image -- it is what forced colours and any engine that drops the gradient
 * fall back to, and it is the whole button on a theme that takes no gradient.
 */
export const gradientFillSx = (displayConfig, color, { hover = true } = {}) => {
  if (!color) return {};

  const ends = gradientEnds(displayConfig, color);

  if (!ends) {
    return {
      backgroundColor: color,
      ...(hover ? { "&:hover": { backgroundColor: darkened(color, 15) } } : {}),
    };
  }

  return {
    backgroundColor: ends.base,
    backgroundImage: `linear-gradient(135deg, ${ends.base} 0%, ${ends.lit} 100%)`,
    ...(hover
      ? {
          "&:hover": {
            backgroundImage: `linear-gradient(135deg, ${darkened(ends.base, 12)} 0%, ${darkened(ends.lit, 12)} 100%)`,
          },
        }
      : {}),
    "@media (forced-colors: active)": { backgroundImage: "none" },
  };
};

/**
 * Section headings, everywhere one is written, and nothing else — no rule under it, no
 * decoration. One definition so every heading on the site matches.
 *
 * On a dark theme a heading is the tenant's action colour, gradient-filled.
 * Paper letters it in plain ink — the tenant's own text colour — and keeps its brand colour
 * for what can be clicked.
 *
 * `color` is only for a heading that means something other than "this is a section" — the
 * error page's red. Everything else takes the default.
 */
export const sectionTitleSx = (displayConfig, color) => {
  const defaultColor = isDarkTheme(displayConfig)
    ? displayConfig?.buttonColor
    : displayConfig?.color;

  return gradientTextSx(displayConfig, color || defaultColor);
};
